// =============================================================================
// domain/importedTrades.ts — what counts as a parser-imported trade.
// -----------------------------------------------------------------------------
// PURE. One rule, in one place, because "which trades did a parser write" is
// asked by two different tools that must not disagree: Import History (undo one
// batch) and Wipe All Imported (take them all back).
//
// ── 🚨 THERE ARE TWO MARKS, AND THEY DO NOT COVER THE SAME ROWS ──
//
//   `imported`                  a BOOLEAN COLUMN. Beta sets it, and has set it
//                               since the beginning.
//   `extras.importBatchId`      a stamp THIS client's importer adds, so one
//                               import can be undone as a unit.
//
// Batch stamping is newer than the book. `io/sync/importBatches.ts` says so in
// its own header — trades that predate it "never cross the wire", and on a book
// where only recent imports are stamped that is most of it.
//
// 🔴 SO THE TWO TOOLS ANSWER DIFFERENT QUESTIONS, AND THAT IS DELIBERATE:
//
//   undo one import   → `batchId`, because a batch is the unit of that decision
//                       and an unstamped trade belongs to no batch
//   wipe all imported → `imported`, because the point of the control is to reach
//                       the rows nothing else can, and scoping it to stamped
//                       batches would leave the oldest imports behind while
//                       saying "all"
//
// A wipe on `batchId` would be the quiet kind of wrong: it would succeed, report
// a plausible number, and leave the legacy imports exactly where they were.
//
// ── 🚨 AND A SYNTHETIC CLOSE IS NEVER WIPED, THOUGH IT IS MARKED IMPORTED ──
//
// A synthetic close is the app's own reconstruction of an exit the broker file
// never contained — an unfinished position the user resolved. It carries
// `imported` because it came out of an import, but **it is not a row any file
// can put back**: re-dragging the CSV produces the open position again, not the
// close. Beta excludes it for exactly this reason
// (`wipeAndRescanAllImported`, app/index.html), and its Backup pane blurb
// promises it: "Manual + synthetic-close trades preserved."
//
// ⚠ NOTHING IN THIS CLIENT CREATES ONE. They are written by beta and by the
// ingest Edge Function into the database both clients share (D5), and read here
// — so this rule is protecting rows this client cannot regenerate.
// =============================================================================

/** The two marks and the one exemption, off a raw `trades` row. */
export interface ImportMarks {
  imported: boolean;
  syntheticClose: boolean;
}

/**
 * ⚠ `=== true`, not truthy, on both. These arrive from PostgREST as real
 * booleans or as `null` for a row written before the column existed, and `null`
 * must read as "no" on BOTH — for `imported` because an unmarked row is the
 * user's own hand-entry, and for `syntheticClose` because beta's `!t.synthetic`
 * treats null the same way. Getting the second one backwards would EXEMPT every
 * legacy row from the wipe and report a count nobody can reconcile.
 */
export function readMarks(row: Record<string, unknown>): ImportMarks {
  return {
    imported: row['imported'] === true,
    syntheticClose: row['synthetic_close'] === true,
  };
}

/**
 * Does "Wipe All Imported" take this row?
 *
 * Beta's predicate exactly: `t.imported && !t.syntheticClose`.
 */
export function isWipeable(m: ImportMarks): boolean {
  return m.imported && !m.syntheticClose;
}

/** What a row is, for the sentence the confirmation has to say. */
export type TradeOrigin = 'imported' | 'synthetic' | 'manual';

export function originOf(m: ImportMarks): TradeOrigin {
  if (m.syntheticClose) return 'synthetic';
  return m.imported ? 'imported' : 'manual';
}

export interface WipeScope {
  /** Ids that will be deleted. */
  wipeIds: string[];
  /** Kept: the app's own reconstructions of an exit no file contains. */
  synthetic: number;
  /** Kept: everything the trader typed in themselves. */
  manual: number;
}

/**
 * Partition the book.
 *
 * ⚠ RETURNS IDS, not just a count, and the two are the same decision. The delete
 * is issued against exactly the ids counted here — `io/sync/importBatches.ts`
 * states the doctrine for the sibling control and it holds harder for this one:
 * **the ids are the consent.** A delete that re-derives its own target between
 * the confirmation and the write can remove more than the number the user read.
 */
export function planWipe(
  rows: readonly Record<string, unknown>[],
): WipeScope {
  const scope: WipeScope = { wipeIds: [], synthetic: 0, manual: 0 };
  for (const r of rows) {
    const marks = readMarks(r);
    switch (originOf(marks)) {
      case 'synthetic': scope.synthetic += 1; break;
      case 'manual': scope.manual += 1; break;
      case 'imported': {
        const id = r['id'];
        // ⚠ A row with no id cannot be deleted and must not be COUNTED as
        // deletable either — the confirmation would promise a number the write
        // cannot reach.
        if (typeof id === 'string' && id) scope.wipeIds.push(id);
        break;
      }
    }
  }
  return scope;
}
